// src/pages/Premios.jsx


import React, { useEffect, useMemo, useState } from "react";
import axios, { BASE_URL } from "../axiosConfig";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import ComProtecao from "../components/ComProtecao";
import { toast } from "react-toastify";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  FileSpreadsheet,
  Gift,
  Plus,
  Search,
  Trash2,
} from "lucide-react";

import "./Premios.css";

const getImagemUrl = (img) => {
  if (!img) return "";
  if (img.startsWith("http://localhost:3001")) {
    return img.replace("http://localhost:3001", BASE_URL);
  }
  if (/^http:\/\/localhost:\d+/.test(img)) {
    return img.replace(/^http:\/\/localhost:\d+/, BASE_URL);
  }
  return img.startsWith("http") ? img : `${BASE_URL}${img}`;
};

function PremiosConteudo() {
  const [premios, setPremios] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const [busca, setBusca] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [filtroFreteGratis, setFiltroFreteGratis] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [descricoesAbertas, setDescricoesAbertas] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);

  const itensPorPagina = 10;

  useEffect(() => {
    buscarPremios();
    buscarCategorias();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, dataInicio, dataFim, categoriaFiltro, filtroFreteGratis]);

  const buscarPremios = () => {
    axios
      .get("/premios")
      .then((response) => setPremios(Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        console.error("Erro ao buscar prêmios:", error);
        setMensagem("Erro ao carregar prêmios.");
      });
  };

  const buscarCategorias = async () => {
    try {
      const res = await axios.get("/categorias-premios");
      setCategorias(res.data || []);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
    }
  };

  const excluirPremio = (id) => {
    if (window.confirm("Tem certeza que deseja excluir este prêmio?")) {
      axios
        .delete(`/premios/${id}`)
        .then(() => {
          setMensagem("Prêmio excluído com sucesso!");
          buscarPremios();
        })
        .catch((error) => {
          console.error("Erro ao excluir prêmio:", error);
          setMensagem("Erro ao excluir prêmio.");
        });
    }
  };

  const alternarFreteGratis = async (id, frete_gratis_atual) => {
    try {
      const novoValor = frete_gratis_atual === 1 ? 0 : 1;

      await axios.put(`/premios/${id}/frete`, { frete_gratis: novoValor });

      setPremios((prev) =>
        prev.map((p) => (p.id === id ? { ...p, frete_gratis: novoValor } : p))
      );

      toast.success("Frete atualizado com sucesso!");
      setTimeout(() => buscarPremios(), 300);
    } catch (erro) {
      console.error("Erro ao atualizar frete grátis:", erro);
      toast.error("Erro ao atualizar frete.");
    }
  };

  const atualizarCategoria = async (id, categoria) => {
    try {
      await axios.put(`/premios/${id}/categoria`, { categoria });

      setPremios((prev) =>
        prev.map((p) => (p.id === id ? { ...p, categoria } : p))
      );

      toast.success("Categoria atualizada.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao atualizar categoria.");
    }
  };

  const exportarParaExcel = () => {
    const dados = premios.map((p) => ({
      ID: p.id,
      Nome: p.nome,
      Descrição: p.descricao,
      "Pontos Necessários": p.valor_em_pontos,
      "Pontos Promocionais": p.valor_em_pontos_promocional || "-",
      "Quantidade em Estoque": p.estoque,
      "Peso (kg)": p.peso_kg || "-",
      "Comprimento (cm)": p.comprimento_cm || "-",
      "Largura (cm)": p.largura_cm || "-",
      "Altura (cm)": p.altura_cm || "-",
      "Frete Grátis?": p.frete_gratis ? "Sim" : "Não",
      "Cadastrado por": p.nome_usuario,
      "Data de Cadastro": p.data_criacao
        ? format(new Date(p.data_criacao), "dd/MM/yyyy")
        : "",
    }));

    const planilha = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, planilha, "Prêmios");
    XLSX.writeFile(wb, "premios.xlsx");
  };

  const premiosFiltrados = useMemo(() => {
    return premios.filter((premio) => {
      const nomeCondiz = (premio.nome || "")
        .toLowerCase()
        .includes(busca.toLowerCase());

      const categoriaCondiz =
        !categoriaFiltro || premio.categoria === categoriaFiltro;

      const data = new Date(premio.data_criacao);

      const dentroPeriodo =
        (!dataInicio || new Date(dataInicio) <= data) &&
        (!dataFim || new Date(dataFim) >= data);

      const freteCondiz =
        !filtroFreteGratis ||
        (filtroFreteGratis === "sim" && Number(premio.frete_gratis) === 1) ||
        (filtroFreteGratis === "nao" && Number(premio.frete_gratis) === 0);

      return nomeCondiz && categoriaCondiz && dentroPeriodo && freteCondiz;
    });
  }, [premios, busca, dataInicio, dataFim, categoriaFiltro, filtroFreteGratis]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(premiosFiltrados.length / itensPorPagina)
  );

  const premiosPagina = premiosFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const resumo = useMemo(() => {
    const total = premios.length;
    const estoqueBaixo = premios.filter((p) => Number(p.estoque || 0) < 10).length;
    const freteGratis = premios.filter((p) => Number(p.frete_gratis || 0) === 1).length;
    const categoriasUsadas = new Set(
      premios.map((p) => p.categoria).filter(Boolean)
    ).size;

    return {
      total,
      estoqueBaixo,
      freteGratis,
      categoriasUsadas,
    };
  }, [premios]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data || isNaN(new Date(data))) return "Não informado";
    return format(new Date(data), "dd/MM/yyyy");
  };

  const toggleDescricao = (id) => {
    setDescricoesAbertas((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderDescricao = (premio) => {
    const descricao = premio.descricao || "Sem descrição.";
    const aberta = !!descricoesAbertas[premio.id];
    const deveTruncar = descricao.length > 120;

    return (
      <div className="premiosAdmin-descricao">
        <p className={deveTruncar && !aberta ? "truncada" : ""}>{descricao}</p>

        {deveTruncar && (
          <button
            type="button"
            className="premiosAdmin-descricaoToggle"
            onClick={() => toggleDescricao(premio.id)}
          >
            {aberta ? (
              <>
                <ChevronUp size={14} />
                Recolher
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                Continuar lendo
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="premiosAdmin-page">
      <div className="premiosAdmin-topbar">
        <div>
          <span className="premiosAdmin-kicker">Catálogo de recompensas</span>

          <h2>Lista de Prêmios</h2>

          <p>
            Gerencie os prêmios disponíveis no catálogo, estoque, categorias,
            frete grátis e valores promocionais.
          </p>
        </div>

        <Link to="/premios/cadastrar" className="premiosAdmin-btn primary">
          <Plus size={16} />
          Novo Prêmio
        </Link>
      </div>

      {mensagem && <div className="premiosAdmin-alert">{mensagem}</div>}

      <div className="premiosAdmin-cards">
        <div className="premiosAdmin-card">
          <span>Total de prêmios</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="premiosAdmin-card warning">
          <span>Estoque baixo</span>
          <strong>{formatarNumero(resumo.estoqueBaixo)}</strong>
        </div>

        <div className="premiosAdmin-card success">
          <span>Frete grátis</span>
          <strong>{formatarNumero(resumo.freteGratis)}</strong>
        </div>

        <div className="premiosAdmin-card info">
          <span>Categorias usadas</span>
          <strong>{formatarNumero(resumo.categoriasUsadas)}</strong>
        </div>
      </div>

      <div className="premiosAdmin-filtrosCard">
        <div className="premiosAdmin-filtrosHeader">
          <div>
            <h3>Filtros</h3>
            <span>Refine a listagem por nome, período, categoria ou frete.</span>
          </div>

          <Gift size={18} />
        </div>

        <div className="premiosAdmin-filtros">
          <div className="premiosAdmin-filtro busca">
            <label>Buscar prêmio</label>
            <div className="premiosAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Buscar por nome..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>

          <div className="premiosAdmin-filtro">
            <label>Data início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>

          <div className="premiosAdmin-filtro">
            <label>Data fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>

          <div className="premiosAdmin-filtro">
            <label>Frete grátis</label>
            <select
              value={filtroFreteGratis}
              onChange={(e) => setFiltroFreteGratis(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </div>

          <div className="premiosAdmin-filtro">
            <label>Categoria</label>
            <select
              value={categoriaFiltro}
              onChange={(e) => setCategoriaFiltro(e.target.value)}
            >
              <option value="">Todas as Categorias</option>
              {categorias
                .filter((cat) => cat.ativa)
                .map((cat) => (
                  <option key={cat.id} value={cat.nome}>
                    {cat.nome}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="premiosAdmin-filtrosActions">
          <button
            type="button"
            className="premiosAdmin-btn secondary"
            onClick={() => {
              setBusca("");
              setDataInicio("");
              setDataFim("");
              setCategoriaFiltro("");
              setFiltroFreteGratis("");
            }}
          >
            Limpar filtros
          </button>

          <div className="premiosAdmin-filtrosRight">
            <span>{formatarNumero(premiosFiltrados.length)} resultado(s)</span>

            <button
              type="button"
              className="premiosAdmin-btn primary"
              onClick={exportarParaExcel}
            >
              <FileSpreadsheet size={16} />
              Exportar Excel
            </button>
          </div>
        </div>
      </div>

      <div className="premiosAdmin-tableCard">
        <div className="premiosAdmin-tableHeader">
          <div>
            <h3>Prêmios cadastrados</h3>
            <span>
              Descrições longas ficam recolhidas para manter a tabela compacta.
            </span>
          </div>
        </div>

        <div className="premiosAdmin-tableWrapper">
          <table className="premiosAdmin-table">
            <thead>
              <tr>
                <th>Prêmio</th>
                <th>Descrição</th>
                <th>Categoria / Voltagem</th>
                <th>Pontos</th>
                <th>Estoque</th>
                <th>Frete</th>
                <th>Cadastro</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {premiosPagina.length === 0 ? (
                <tr>
                  <td colSpan="8" className="premiosAdmin-empty">
                    Nenhum prêmio encontrado.
                  </td>
                </tr>
              ) : (
                premiosPagina.map((premio) => {
                  const estoqueBaixo = Number(premio.estoque || 0) < 10;

                  return (
                    <tr key={premio.id}>
                      <td>
                        <div className="premiosAdmin-produtoCell">
                          {premio.imagem ? (
                            <img
                              src={getImagemUrl(premio.imagem)}
                              alt={premio.nome}
                              className="premiosAdmin-miniatura"
                            />
                          ) : (
                            <div className="premiosAdmin-miniatura vazio">
                              <Gift size={18} />
                            </div>
                          )}

                          <div className="premiosAdmin-cellMain">
                            <strong>{premio.nome}</strong>
                            <span>ID #{premio.id}</span>
                          </div>
                        </div>
                      </td>

                      <td>{renderDescricao(premio)}</td>

                      <td>
                        <div className="premiosAdmin-cellMain">
                          <select
                            value={premio.categoria || ""}
                            onChange={(e) =>
                              atualizarCategoria(premio.id, e.target.value)
                            }
                            className="premiosAdmin-selectCategoria"
                          >
                            <option value="">Sem categoria</option>
                            {categorias
                              .filter((cat) => cat.ativa)
                              .map((cat) => (
                                <option key={cat.id} value={cat.nome}>
                                  {cat.nome}
                                </option>
                              ))}
                          </select>

                          <span>
                            {Number(premio.exige_voltagem || 0) === 1
                              ? premio.voltagens_disponiveis || "Voltagem obrigatória"
                              : "Voltagem: não se aplica"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="premiosAdmin-pontosBox">
                          {premio.valor_em_pontos_promocional ? (
                            <>
                              <span className="premiosAdmin-pontosOriginal">
                                {premio.valor_em_pontos?.toLocaleString("pt-BR")} pts
                              </span>

                              <strong className="premiosAdmin-pontosPromo">
                                {premio.valor_em_pontos_promocional?.toLocaleString(
                                  "pt-BR"
                                )}{" "}
                                pts
                              </strong>
                            </>
                          ) : (
                            <strong>
                              {premio.valor_em_pontos?.toLocaleString("pt-BR")} pts
                            </strong>
                          )}
                        </div>
                      </td>

                      <td>
                        <span
                          className={`premiosAdmin-estoque ${
                            estoqueBaixo ? "baixo" : ""
                          }`}
                        >
                          {estoqueBaixo && <span />}
                          {premio.estoque}
                        </span>
                      </td>

                      <td>
                        <label className="premiosAdmin-switch">
                          <input
                            type="checkbox"
                            checked={Number(premio.frete_gratis) === 1}
                            onChange={() =>
                              alternarFreteGratis(premio.id, premio.frete_gratis)
                            }
                          />
                          <span className="premiosAdmin-slider" />
                        </label>
                      </td>

                      <td>
                        <div className="premiosAdmin-cellMain">
                          <strong>{premio.nome_usuario || "-"}</strong>
                          <span>{formatarData(premio.data_criacao)}</span>
                        </div>
                      </td>

                      <td>
                        <div className="premiosAdmin-acoes">
                          <Link
                            to={`/editar-premio/${premio.id}`}
                            className="premiosAdmin-actionBtn edit"
                          >
                            <Edit size={14} />
                            Editar
                          </Link>

                          <button
                            type="button"
                            className="premiosAdmin-actionBtn delete"
                            onClick={() => excluirPremio(premio.id)}
                          >
                            <Trash2 size={14} />
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="premiosAdmin-paginacao">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Premios() {
  return (
    <ComProtecao rota="premios" tipo="pode_ler">
      <PremiosConteudo />
    </ComProtecao>
  );
}